import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Flex, useBreakpointValue } from '@chakra-ui/react';
import { Modal, Button } from '@morressier/alembic';
import _ from 'lodash';
import { IFiguresAndTablesFile } from '@publishing-engine/domain/models/submission';
import { generateValidationSchema } from 'src/utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { FormFieldGraphicalAbstractImage } from '@publishing-engine/domain/types/article';
import GraphicalAbstractImage, {
  IGraphicalAbstractImageValue
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/GraphicalAbstractImage';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: IGraphicalAbstractImageValue) => void;
  formField: FormFieldGraphicalAbstractImage;
  value?: IFiguresAndTablesFile;
  submissionContextId: string;
}

const GraphicalAbstractImageUpdateModal: React.FC<Props> = props => {
  const { isOpen, onClose, onSubmit, formField, submissionContextId } = props;
  const modalWidth = useBreakpointValue({ base: '95vw', md: '955px' });

  const defaultValues = _.set(
    {} as IGraphicalAbstractImageValue,
    formField.caseDataFields.main,
    props.value
  );

  const schema = generateValidationSchema({
    graphicalAbstractImage: formField
  });

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const validateAndSubmit = (data: IGraphicalAbstractImageValue) => {
    if (!validateGraphicalAbstractImage(data)) {
      scrollToFirstError();
      return;
    }
    onSubmit(data);
    onClose();
  };

  const validateGraphicalAbstractImage = (
    data: IGraphicalAbstractImageValue
  ): boolean => {
    let isValid = true;

    methods.clearErrors('fileSet.GraphicalAbstractImage');

    const images = data.fileSet?.GraphicalAbstractImage;

    if (!images || images.length === 0) return true;

    const allEmpty = images.every((image: any) => {
      const noCaption = !image.caption || image.caption.trim() === '';
      const noFile = !image.file;
      return noCaption && noFile;
    });

    if (allEmpty) return true;

    const seenCaptions = new Set<string>();

    images.forEach((image: any, idx: number) => {
      const captionRaw = image.caption ?? '';
      const caption = captionRaw.trim();
      if (caption !== '') {
        const normalized = caption.toLowerCase();
        if (seenCaptions.has(normalized)) {
          methods.setError(`fileSet.GraphicalAbstractImage.${idx}.caption`, {
            type: 'manual',
            message: 'Duplicate caption not allowed for graphical abstract images.'
          });
          isValid = false;
        } else {
          seenCaptions.add(normalized);
        }
      }
    });

    return isValid;
  };

  const checkIsFileLoading = () => {
    const values = methods.getValues();

    return (
      values.fileSet?.GraphicalAbstractImage?.some(
        image => image.file?.isUploading
      ) ?? false
    );
  };

  const isFileUploading = checkIsFileLoading();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={modalWidth}
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(data => {
            validateAndSubmit(data);
          })}
        >
          <Flex
            gap={4}
            bgColor="surfacePrimaryFill"
            flexDirection="column"
            borderRadius="base"
            alignItems="center"
            padding={{ base: 3, md: 4 }}
          >
            <Flex width="100%" alignSelf="center">
              <GraphicalAbstractImage
                config={formField}
                submissionId={submissionContextId}
                isFromEditModal={true}
              />
            </Flex>
            <Flex
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf="stretch"
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
            >
              <Button
                variant="secondary"
                onClick={onClose}
                borderRadius="small"
                disabled={isFileUploading}
              >
                Exit Without Saving
              </Button>
              <Button
                variant="primary"
                type="submit"
                borderRadius="small"
                disabled={isFileUploading}
              >
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default GraphicalAbstractImageUpdateModal;
